import { useEffect, useId, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import DateInput, { DATE_INPUT_FIELD_CLASS } from './DateInput'
import { isDdMmYyyyInLocalRange, PORTAL_DATE_MIN, startOfTodayLocal } from '../utils/dobDateInput'
import {
  CLAIM_FORM_FIELD_ERROR_BORDER_CLASS,
  ClaimFormSubmitSummaryAlert,
} from '../claims/claimFormValidationFeedback'
import { isIncidentDdMmYyyyBeforePolicyStart, parsePolicyCoverStartLocalDay } from '../utils/policyCoverStart'
import { ChevronRightIcon, FormCard, PrimaryButton } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherTowingHref,
  buildClaimOtherFireDriverHref,
  buildClaimOtherMaliciousDamageDescribeHref,
  buildClaimOtherOtherLossDescribeHref,
  buildClaimOtherMultiVehicleDriverHref,
  buildClaimOtherSingleVehicleDriverHref,
  buildClaimOtherSubtypeHref,
  buildClaimOtherTheftDriverHref,
  buildClaimOtherWeatherDriverHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
  parseClaimBranchQuery,
  type ClaimEntryBranch,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery } from '../claims/otherClaimSubtypeOptions'
import { CLAIM_FORM_CONTROL_CLASS, CLAIM_FORM_SELECT_CLASS } from '../claims/claimFormFieldClasses'
import {
  combineHm24,
  INCIDENT_QUARTER_MINUTES,
  isQuarterMinuteToken,
  splitHm24,
} from '../claims/incidentQuarterHourTime'
import { INCIDENT_TIME_MOBILE_MEDIA_QUERY, useMatchMedia } from '../hooks/useMatchMedia'
import IncidentQuarterHourWheelPicker from './IncidentQuarterHourWheelPicker'
import { persistIncidentSnapshot, readClaimDraft } from '../claims/claimDraftStorage'
import {
  INCIDENT_ROUGH_LOCATION_MAX_LEN,
  INCIDENT_STREET_MAX_LEN,
  INCIDENT_SUBURB_MAX_LEN,
  sanitizeIncidentWhenWhereText,
} from '../utils/claimIncidentTextSanitize'
import { validateAuPostcodeFormat, validatePostcodeStateMatch } from '../utils/validation'

const AU_STATES = [
  { value: '', label: 'Select' },
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
]

const WINDSCREEN_HOURS_24 = Array.from({ length: 24 }, (_, i) => i)

/**
 * Nearest quarter in the current hour, or top of the next hour (`minute` 0, `carryHour` 1).
 */
function snapMinuteToNearestQuarter(min: number): { minute: number; carryHour: 0 | 1 } {
  if (min < 0 || min > 59) return { minute: 0, carryHour: 0 }
  let bestQ = 0
  let bestDist = Math.abs(min - 0)
  for (const q of [15, 30, 45] as const) {
    const d = Math.abs(min - q)
    if (d < bestDist) {
      bestDist = d
      bestQ = q
    }
  }
  const distToNextHour = 60 - min
  if (distToNextHour < bestDist) {
    return { minute: 0, carryHour: 1 }
  }
  return { minute: bestQ, carryHour: 0 }
}

/** Normalizes a 24-hour HH:MM string to quarter minutes; hour wraps 23→0 on carry. */
function normalizeToQuarterHourHm24(s: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim())
  if (!m) return s
  let h = Number(m[1])
  const minNum = Number(m[2])
  if (h < 0 || h > 23 || minNum > 59) return s
  const { minute: snappedMin, carryHour } = snapMinuteToNearestQuarter(minNum)
  if (carryHour === 1) {
    h = (h + 1) % 24
  }
  return `${String(h).padStart(2, '0')}:${String(snappedMin).padStart(2, '0')}`
}

function clock12To24Hour(h12: number, ap: 'AM' | 'PM'): number {
  if (ap === 'AM') {
    return h12 === 12 ? 0 : h12
  }
  return h12 === 12 ? 12 : h12 + 12
}

function clock24To12hour(h24: number): { h12: number; ap: 'AM' | 'PM' } {
  if (h24 === 0) return { h12: 12, ap: 'AM' }
  if (h24 < 12) return { h12: h24, ap: 'AM' }
  if (h24 === 12) return { h12: 12, ap: 'PM' }
  return { h12: h24 - 12, ap: 'PM' }
}

/** Windscreen UI string (24h) → draft fields (12h clock + AM/PM). */
function windscreenUiToDraft12h(
  ui: string,
  fallbackAmPm: 'AM' | 'PM'
): { incidentTimeHm: string; incidentTimeAmPm: 'AM' | 'PM' } | null {
  const t = ui.trim()
  if (!t) return null
  const partialH = /^(\d{1,2}):--$/.exec(t)
  if (partialH) {
    const h24 = Number(partialH[1])
    if (h24 < 0 || h24 > 23) return null
    const { h12, ap } = clock24To12hour(h24)
    return { incidentTimeHm: `${String(h12).padStart(2, '0')}:--`, incidentTimeAmPm: ap }
  }
  const partialM = /^--:(\d{2})$/.exec(t)
  if (partialM) {
    return { incidentTimeHm: `--:${partialM[1]}`, incidentTimeAmPm: fallbackAmPm }
  }
  const full = /^(\d{1,2}):(\d{2})$/.exec(t)
  if (!full) return null
  const h24 = Number(full[1])
  const mm = full[2]
  if (h24 < 0 || h24 > 23 || Number(mm) > 59) return null
  const { h12, ap } = clock24To12hour(h24)
  return { incidentTimeHm: `${String(h12).padStart(2, '0')}:${mm}`, incidentTimeAmPm: ap }
}

/** Draft (12h + AM/PM) → windscreen UI string (24h, partials preserved). */
function draft12hToWindscreenUi(draftHm: string, ap: 'AM' | 'PM'): string {
  const t = draftHm.trim()
  if (!t) return ''
  const partialH = /^(\d{1,2}):--$/.exec(t)
  if (partialH) {
    const h12 = Number(partialH[1])
    if (h12 < 1 || h12 > 12) return t
    const h24 = clock12To24Hour(h12, ap)
    return `${String(h24).padStart(2, '0')}:--`
  }
  if (/^--:\d{2}$/.test(t)) return t
  const full = /^(\d{1,2}):(\d{2})$/.exec(t)
  if (!full) return t
  const h12 = Number(full[1])
  const mm = full[2]
  if (h12 < 1 || h12 > 12 || Number(mm) > 59) return t
  const h24 = clock12To24Hour(h12, ap)
  return `${String(h24).padStart(2, '0')}:${mm}`
}

function getInitialIncidentClock(
  branch: ClaimEntryBranch | null,
  draftHm: string | null | undefined,
  draftAmPm: 'AM' | 'PM' | null | undefined
): { incidentTimeHm: string; incidentAmPm: 'AM' | 'PM' } {
  const raw = draftHm ?? ''
  const amPm: 'AM' | 'PM' = draftAmPm === 'PM' ? 'PM' : 'AM'
  const useQuarterHour24Ui = branch === 'windscreen' || branch === 'other'
  if (!useQuarterHour24Ui || !raw.trim()) {
    return { incidentTimeHm: raw, incidentAmPm: amPm }
  }
  const ui = draft12hToWindscreenUi(raw.trim(), amPm)
  const normalized = normalizeToQuarterHourHm24(ui)
  const draftBack = windscreenUiToDraft12h(normalized, amPm)
  return {
    incidentTimeHm: normalized,
    incidentAmPm: draftBack?.incidentTimeAmPm ?? amPm,
  }
}

function isValidIncidentDateDdMmYyyy(s: string): boolean {
  return isDdMmYyyyInLocalRange(s.trim(), PORTAL_DATE_MIN, startOfTodayLocal())
}

function isValidQuarterHourTimeHm24(s: string): boolean {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim())
  if (!m) return false
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min > 59) return false
  return min === 0 || min === 15 || min === 30 || min === 45
}

/**
 * Collects when and where the incident occurred after the user chooses windscreen vs other
 * before GST / towing branch steps.
 */
export default function ClaimIncidentStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const dateId = useId()
  const timeId = useId()
  const hourId = useId()
  const minuteId = useId()
  const streetId = useId()
  const suburbId = useId()
  const postcodeId = useId()
  const roughLocationId = useId()
  const datePolicyErrorId = useId()
  const whenWhereSummaryId = useId()
  const dateFieldErrorId = useId()
  const timeFieldErrorId = useId()
  const stateSelectId = useId()
  const stateFieldErrorId = useId()
  const streetFieldErrorId = useId()
  const suburbFieldErrorId = useId()
  const postcodeFieldErrorId = useId()
  const roughLocationFieldErrorId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimEntryBranch = parseClaimBranchQuery(queryParams.get('branch'))
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const draft = readClaimDraft()
  const otherClaimSubtype =
    claimEntryBranch === 'other'
      ? parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
        parseOtherClaimSubtypeQuery(draft?.otherClaimSubtypeId ?? null)
      : null

  const [incidentDate, setIncidentDate] = useState(() => draft?.incidentDateDdMmYyyy ?? '')
  const [incidentTime, setIncidentTime] = useState(
    () => getInitialIncidentClock(claimEntryBranch, draft?.incidentTimeHm, draft?.incidentTimeAmPm).incidentTimeHm
  )
  const [incidentAmPm, setIncidentAmPm] = useState<'AM' | 'PM'>(
    () => getInitialIncidentClock(claimEntryBranch, draft?.incidentTimeHm, draft?.incidentTimeAmPm).incidentAmPm
  )
  const [street, setStreet] = useState(() =>
    sanitizeIncidentWhenWhereText(draft?.incidentStreet ?? '', { maxLen: INCIDENT_STREET_MAX_LEN })
  )
  const [suburb, setSuburb] = useState(() =>
    sanitizeIncidentWhenWhereText(draft?.incidentSuburb ?? '', { maxLen: INCIDENT_SUBURB_MAX_LEN })
  )
  const [postcode, setPostcode] = useState(() => draft?.incidentPostcode ?? '')
  const [state, setState] = useState(() => draft?.incidentState ?? '')
  const [addressUnknown, setAddressUnknown] = useState(() => draft?.incidentAddressUnknown === true)
  const [roughLocation, setRoughLocation] = useState(
    () =>
      draft?.incidentAddressUnknown
        ? sanitizeIncidentWhenWhereText(draft?.incidentAddressSearch ?? '', {
            maxLen: INCIDENT_ROUGH_LOCATION_MAX_LEN,
            multiline: true,
          })
        : ''
  )
  const [whenWhereSubmitAttempted, setWhenWhereSubmitAttempted] = useState(false)

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const policyCoverStartLocal = useMemo(
    () => parsePolicyCoverStartLocalDay(navState?.policy),
    [navState?.policy]
  )

  const incidentDateMinDate = useMemo(() => {
    if (!policyCoverStartLocal) return undefined
    const p = new Date(
      policyCoverStartLocal.getFullYear(),
      policyCoverStartLocal.getMonth(),
      policyCoverStartLocal.getDate()
    ).getTime()
    const portal = new Date(
      PORTAL_DATE_MIN.getFullYear(),
      PORTAL_DATE_MIN.getMonth(),
      PORTAL_DATE_MIN.getDate()
    ).getTime()
    return new Date(Math.max(p, portal))
  }, [policyCoverStartLocal])

  const incidentDateBeforePolicyStart = useMemo(
    () => isIncidentDdMmYyyyBeforePolicyStart(incidentDate, navState?.policy),
    [incidentDate, navState?.policy]
  )

  const incidentLiveSummary = useMemo(() => {
    const t = incidentTime.trim()
    const timeForRail = isValidQuarterHourTimeHm24(t) ? t : null
    return formatIncidentRailSummary({
      incidentDateDdMmYyyy: incidentDate,
      incidentTimeHm: timeForRail,
      incidentTimeAmPm: null,
      incidentAddressSearch: roughLocation,
      incidentStreet: street,
      incidentSuburb: suburb,
      incidentState: state,
      incidentPostcode: postcode,
      incidentAddressUnknown: addressUnknown,
    })
  }, [incidentDate, incidentTime, roughLocation, street, suburb, state, postcode, addressUnknown])

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimEntryBranch !== 'other' || otherClaimSubtype !== null) return
    const path = buildClaimOtherSubtypeHref(policyRef, typeInQuery)
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimEntryBranch, navigate, navState?.policy, otherClaimSubtype, policyRef, typeInQuery])

  useEffect(() => {
    if (claimEntryBranch !== null) return
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.selectType}?${q}` : CLAIM_FLOW_PATH.selectType
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimEntryBranch, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimEntryBranch !== 'windscreen' && claimEntryBranch !== 'other') return
    const trimmed = incidentTime.trim()
    if (!trimmed) return
    const mapped = windscreenUiToDraft12h(trimmed, incidentAmPm)
    if (!mapped || mapped.incidentTimeAmPm === incidentAmPm) return
    setIncidentAmPm(mapped.incidentTimeAmPm)
  }, [claimEntryBranch, incidentTime, incidentAmPm])

  useEffect(() => {
    const t = window.setTimeout(() => {
      const quarterMapped =
        claimEntryBranch === 'windscreen' || claimEntryBranch === 'other'
          ? windscreenUiToDraft12h(incidentTime.trim(), incidentAmPm)
          : null
      persistIncidentSnapshot(policyRef, {
        incidentDateDdMmYyyy: incidentDate || null,
        incidentTimeHm: (quarterMapped?.incidentTimeHm ?? incidentTime) || null,
        incidentTimeAmPm: quarterMapped?.incidentTimeAmPm ?? incidentAmPm,
        ...(addressUnknown
          ? {
              incidentAddressSearch: roughLocation.trim() || null,
              incidentStreet: null,
              incidentSuburb: null,
              incidentPostcode: null,
              incidentState: state.trim() || null,
            }
          : {
              incidentAddressSearch: null,
              incidentStreet: street || null,
              incidentSuburb: suburb || null,
              incidentPostcode: postcode || null,
              incidentState: state || null,
            }),
        incidentAddressUnknown: addressUnknown,
      })
    }, 400)
    return () => window.clearTimeout(t)
  }, [
    claimEntryBranch,
    policyRef,
    incidentDate,
    incidentTime,
    incidentAmPm,
    street,
    suburb,
    postcode,
    state,
    roughLocation,
    addressUnknown,
  ])

  const handleBack = () => {
    const statePolicy = navState?.policy ? { policy: navState.policy } : undefined
    if (claimEntryBranch === 'other') {
      const path = buildClaimOtherSubtypeHref(policyRef, typeInQuery)
      navigate(path, { state: statePolicy })
      return
    }
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.selectType}?${q}` : CLAIM_FLOW_PATH.selectType
    navigate(path, { state: statePolicy })
  }

  const canProceed = useMemo(() => {
    if (!isValidIncidentDateDdMmYyyy(incidentDate.trim())) return false
    if (incidentDateBeforePolicyStart) return false
    const t = incidentTime.trim()
    if (!isValidQuarterHourTimeHm24(t)) return false
    if (addressUnknown) {
      return Boolean(state.trim() && roughLocation.trim())
    }
    const pc = postcode.trim()
    const st = state.trim()
    if (!street.trim() || !suburb.trim() || !validateAuPostcodeFormat(pc) || !st) {
      return false
    }
    if (!validatePostcodeStateMatch(pc, st)) return false
    return true
  }, [incidentDate, incidentDateBeforePolicyStart, incidentTime, addressUnknown, street, suburb, postcode, state, roughLocation])

  const whenWhereFieldErrors = useMemo(() => {
    if (!whenWhereSubmitAttempted) {
      return {
        date: false,
        time: false,
        state: false,
        street: false,
        suburb: false,
        postcode: false,
        postcodeMismatch: false,
        location: false,
      }
    }
    const pc = postcode.trim()
    const st = state.trim()
    const postcodeFormatInvalid = !addressUnknown && (!pc || !validateAuPostcodeFormat(pc))
    const postcodeMismatch =
      !addressUnknown && !postcodeFormatInvalid && !!pc && !!st && !validatePostcodeStateMatch(pc, st)
    return {
      date: !isValidIncidentDateDdMmYyyy(incidentDate.trim()),
      time: !isValidQuarterHourTimeHm24(incidentTime.trim()),
      state: !st,
      street: !addressUnknown && !street.trim(),
      suburb: !addressUnknown && !suburb.trim(),
      postcode: postcodeFormatInvalid || postcodeMismatch,
      postcodeMismatch,
      location: addressUnknown && !roughLocation.trim(),
    }
  }, [
    whenWhereSubmitAttempted,
    incidentDate,
    incidentTime,
    state,
    addressUnknown,
    street,
    suburb,
    postcode,
    roughLocation,
  ])

  const showWhenWhereSummary =
    whenWhereSubmitAttempted && !canProceed && !incidentDateBeforePolicyStart

  const dateAriaDescribedBy = [
    whenWhereFieldErrors.date && !incidentDateBeforePolicyStart ? dateFieldErrorId : null,
    incidentDateBeforePolicyStart ? datePolicyErrorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined

  const dateAriaInvalid = Boolean(whenWhereFieldErrors.date || incidentDateBeforePolicyStart)

  const inputErrorClass = CLAIM_FORM_FIELD_ERROR_BORDER_CLASS

  const handleNext = () => {
    if (claimEntryBranch === null) return
    if (claimEntryBranch === 'other' && otherClaimSubtype === null) return
    if (incidentDateBeforePolicyStart) return
    if (!canProceed) {
      setWhenWhereSubmitAttempted(true)
      return
    }
    setWhenWhereSubmitAttempted(false)
    const trimmedTime = incidentTime.trim()
    const quarterMapped =
      claimEntryBranch === 'windscreen' || claimEntryBranch === 'other'
        ? windscreenUiToDraft12h(trimmedTime, incidentAmPm)
        : null
    persistIncidentSnapshot(policyRef, {
      incidentDateDdMmYyyy: incidentDate.trim(),
      incidentTimeHm: quarterMapped?.incidentTimeHm ?? trimmedTime,
      incidentTimeAmPm: quarterMapped?.incidentTimeAmPm ?? incidentAmPm,
      ...(addressUnknown
        ? {
            incidentAddressSearch: roughLocation.trim() || null,
            incidentStreet: null,
            incidentSuburb: null,
            incidentPostcode: null,
            incidentState: state.trim() || null,
          }
        : {
            incidentAddressSearch: null,
            incidentStreet: street.trim() || null,
            incidentSuburb: suburb.trim() || null,
            incidentPostcode: postcode.trim() || null,
            incidentState: state.trim() || null,
          }),
      incidentAddressUnknown: addressUnknown,
    })
    if (claimEntryBranch === 'windscreen') {
      const p = new URLSearchParams()
      if (policyRef) p.set('policyRef', policyRef)
      if (typeInQuery) p.set('type', typeInQuery)
      const q = p.toString()
      const nextPath = q ? `${CLAIM_FLOW_PATH.windscreen}?${q}` : CLAIM_FLOW_PATH.windscreen
      navigate(nextPath, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (otherClaimSubtype === null) return
    const nextPath =
      otherClaimSubtype === 'multi-vehicle-collision'
        ? buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, otherClaimSubtype)
        : otherClaimSubtype === 'malicious-damage'
          ? buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, otherClaimSubtype)
          : otherClaimSubtype === 'other-loss'
            ? buildClaimOtherOtherLossDescribeHref(policyRef, typeInQuery, otherClaimSubtype)
            : otherClaimSubtype === 'weather'
              ? buildClaimOtherWeatherDriverHref(policyRef, typeInQuery, otherClaimSubtype)
              : otherClaimSubtype === 'fire'
                ? buildClaimOtherFireDriverHref(policyRef, typeInQuery, otherClaimSubtype)
                : otherClaimSubtype === 'theft'
                  ? buildClaimOtherTheftDriverHref(policyRef, typeInQuery, otherClaimSubtype)
                  : otherClaimSubtype === 'single-vehicle'
                    ? buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, otherClaimSubtype)
                    : buildClaimOtherTowingHref(policyRef, typeInQuery, otherClaimSubtype)
    navigate(nextPath, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const inputClass = CLAIM_FORM_CONTROL_CLASS

  const incidentHmParts =
    claimEntryBranch === 'windscreen' || claimEntryBranch === 'other'
      ? splitHm24(incidentTime)
      : { hour: '', minute: '' }

  const useMobileIncidentTimeWheel = useMatchMedia(INCIDENT_TIME_MOBILE_MEDIA_QUERY)

  if (claimEntryBranch === null || (claimEntryBranch === 'other' && otherClaimSubtype === null)) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={handleBack}
        title="Tell us what happened"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch={claimEntryBranch ?? undefined}
            otherClaimSubtypeId={claimEntryBranch === 'other' ? otherClaimSubtype : null}
            incidentSummary={incidentLiveSummary}
            multiVehicleCollisionSummary={
              claimEntryBranch === 'other'
                ? formatMultiVehicleCollisionRailSummary(readClaimDraft() ?? undefined)
                : undefined
            }
            multiVehicleOtherPartySummary={
              claimEntryBranch === 'other'
                ? formatMultiVehicleOtherPartyRailSummary(readClaimDraft() ?? undefined)
                : undefined
            }
            multiVehicleIncidentDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.multiVehicleIncidentDescription ?? null : null
            }
            multiVehicleDamageDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.multiVehicleDamageDescription ?? null : null
            }
            multiVehicleClaimDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.multiVehicleClaimDescription ?? null : null
            }
            weatherClaimConditionsDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.weatherClaimConditionsDescription ?? null : null
            }
            fireClaimTypeDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.fireClaimTypeDescription ?? null : null
            }
            theftOrBreakInDetailDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.theftOrBreakInDetailDescription ?? null : null
            }
            singleVehicleCollisionSummary={
              claimEntryBranch === 'other'
                ? formatSingleVehicleCollisionRailSummary(readClaimDraft())
                : undefined
            }
            singleVehicleIncidentDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.singleVehicleIncidentDescription ?? null : null
            }
            singleVehicleDamageDescription={
              claimEntryBranch === 'other' ? readClaimDraft()?.singleVehicleDamageDescription ?? null : null
            }
            windscreenGstRegistered={
              claimEntryBranch === 'other' ? readClaimDraft()?.windscreenGstRegistered ?? null : undefined
            }
            windscreenAbn={claimEntryBranch === 'other' ? readClaimDraft()?.windscreenAbn ?? null : undefined}
            windscreenItcEntitled={
              claimEntryBranch === 'other' ? readClaimDraft()?.windscreenItcEntitled ?? null : undefined
            }
            windscreenItcPercentage={
              claimEntryBranch === 'other' ? readClaimDraft()?.windscreenItcPercentage ?? null : undefined
            }
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col gap-[20px]" aria-labelledby="incident-heading">
            <h2
              id="incident-heading"
              className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              When and where did the incident occur?
            </h2>

            {showWhenWhereSummary ? <ClaimFormSubmitSummaryAlert id={whenWhereSummaryId} /> : null}

            <div className="grid w-full grid-cols-1 gap-[16px] md:grid-cols-2 md:gap-[20px]">
              <div className="flex min-w-0 flex-col gap-[8px]">
                <label htmlFor={dateId} className="font-body text-sm leading-sm text-text-tertiary-600">
                  Date
                </label>
                <DateInput
                  id={dateId}
                  value={incidentDate}
                  onValueChange={setIncidentDate}
                  minDate={incidentDateMinDate}
                  focusLatestSelectableDayWhenEmpty
                  policyStartHighlightDate={policyCoverStartLocal ?? undefined}
                  policyStartLegendLabel="Policy start date"
                  ariaDescribedBy={dateAriaDescribedBy}
                  ariaInvalid={dateAriaInvalid}
                  className={`${DATE_INPUT_FIELD_CLASS} ${
                    whenWhereFieldErrors.date || incidentDateBeforePolicyStart ? 'rounded-md ring-2 ring-red-600' : ''
                  }`.trim()}
                />
                {whenWhereFieldErrors.date && !incidentDateBeforePolicyStart ? (
                  <p
                    id={dateFieldErrorId}
                    role="alert"
                    className="m-0 font-body text-xs leading-xs text-red-700"
                  >
                    Enter a valid incident date in DD/MM/YYYY.
                  </p>
                ) : null}
                {incidentDateBeforePolicyStart ? (
                  <p
                    id={datePolicyErrorId}
                    role="alert"
                    className="m-0 font-body text-xs leading-xs text-red-700"
                  >
                    Incident date cannot be before your policy start date.
                  </p>
                ) : null}
              </div>
              <div className="flex min-w-0 flex-col gap-[8px]">
                <span className="font-body text-sm leading-sm text-text-tertiary-600" id={`${timeId}-label`}>
                  Time
                </span>
                <div
                  className={`flex w-full flex-col gap-[10px] sm:flex-row sm:items-start sm:gap-[14px] ${
                    whenWhereFieldErrors.time ? 'rounded-lg p-[1px] ring-2 ring-red-600' : ''
                  }`.trim()}
                >
                  {useMobileIncidentTimeWheel ? (
                    <IncidentQuarterHourWheelPicker
                      value={incidentTime}
                      onChange={setIncidentTime}
                      triggerId={timeId}
                      hourLabelId={hourId}
                      minuteLabelId={minuteId}
                      describedBy={
                        [whenWhereFieldErrors.time ? timeFieldErrorId : null, `${timeId}-label`]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      invalid={whenWhereFieldErrors.time}
                    />
                  ) : (
                    <div className="flex min-w-0 w-full flex-nowrap items-center gap-[10px] sm:max-w-[min(100%,420px)]">
                      <div className="min-w-0 flex-1">
                        <label htmlFor={hourId} className="sr-only">
                          Hour (24-hour)
                        </label>
                        <select
                          id={hourId}
                          aria-describedby={
                            [whenWhereFieldErrors.time ? timeFieldErrorId : null, `${timeId}-label`]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                          aria-invalid={whenWhereFieldErrors.time}
                          value={incidentHmParts.hour}
                          onChange={(e) =>
                            setIncidentTime(combineHm24(e.target.value, incidentHmParts.minute))
                          }
                          className={CLAIM_FORM_SELECT_CLASS}
                        >
                          <option value="">Hour</option>
                          {WINDSCREEN_HOURS_24.map((h) => (
                            <option key={h} value={String(h)}>
                              {String(h).padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <span
                        className="shrink-0 pt-[2px] font-body text-lg leading-none text-text-tertiary-600"
                        aria-hidden
                      >
                        :
                      </span>
                      <div className="min-w-0 flex-1">
                        <label htmlFor={minuteId} className="sr-only">
                          Minutes
                        </label>
                        <select
                          id={minuteId}
                          aria-describedby={
                            [whenWhereFieldErrors.time ? timeFieldErrorId : null, `${timeId}-label`]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                          aria-invalid={whenWhereFieldErrors.time}
                          value={
                            isQuarterMinuteToken(incidentHmParts.minute) ? incidentHmParts.minute : ''
                          }
                          onChange={(e) =>
                            setIncidentTime(combineHm24(incidentHmParts.hour, e.target.value))
                          }
                          className={CLAIM_FORM_SELECT_CLASS}
                        >
                          <option value="">Min</option>
                          {INCIDENT_QUARTER_MINUTES.map((mm) => (
                            <option key={mm} value={mm}>
                              {mm}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                {whenWhereFieldErrors.time ? (
                  <p
                    id={timeFieldErrorId}
                    role="alert"
                    className="m-0 font-body text-xs leading-xs text-red-700"
                  >
                    Select hour and minutes (15-minute steps) for when the incident occurred.
                  </p>
                ) : null}
              </div>
            </div>

            {!addressUnknown ? (
              <div className="grid w-full grid-cols-1 gap-[16px] md:grid-cols-2 md:gap-[20px]">
                <div className="flex flex-col gap-[8px] md:col-span-2">
                  <label htmlFor={streetId} className="font-body text-sm leading-sm text-text-tertiary-600">
                    Street address
                  </label>
                  <input
                    id={streetId}
                    type="text"
                    value={street}
                    onChange={(e) =>
                      setStreet(
                        sanitizeIncidentWhenWhereText(e.target.value, { maxLen: INCIDENT_STREET_MAX_LEN })
                      )
                    }
                    autoComplete="street-address"
                    aria-invalid={whenWhereFieldErrors.street}
                    aria-describedby={whenWhereFieldErrors.street ? streetFieldErrorId : undefined}
                    className={`${inputClass} ${whenWhereFieldErrors.street ? inputErrorClass : ''}`.trim()}
                  />
                  {whenWhereFieldErrors.street ? (
                    <p
                      id={streetFieldErrorId}
                      role="alert"
                      className="m-0 font-body text-xs leading-xs text-red-700"
                    >
                      Enter the street address.
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-[8px]">
                  <label htmlFor={suburbId} className="font-body text-sm leading-sm text-text-tertiary-600">
                    Suburb
                  </label>
                  <input
                    id={suburbId}
                    type="text"
                    value={suburb}
                    onChange={(e) =>
                      setSuburb(
                        sanitizeIncidentWhenWhereText(e.target.value, { maxLen: INCIDENT_SUBURB_MAX_LEN })
                      )
                    }
                    autoComplete="address-level2"
                    aria-invalid={whenWhereFieldErrors.suburb}
                    aria-describedby={whenWhereFieldErrors.suburb ? suburbFieldErrorId : undefined}
                    className={`${inputClass} ${whenWhereFieldErrors.suburb ? inputErrorClass : ''}`.trim()}
                  />
                  {whenWhereFieldErrors.suburb ? (
                    <p
                      id={suburbFieldErrorId}
                      role="alert"
                      className="m-0 font-body text-xs leading-xs text-red-700"
                    >
                      Enter the suburb.
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-[8px]">
                  <label htmlFor={postcodeId} className="font-body text-sm leading-sm text-text-tertiary-600">
                    Post code
                  </label>
                  <input
                    id={postcodeId}
                    type="text"
                    inputMode="numeric"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    autoComplete="postal-code"
                    maxLength={4}
                    aria-invalid={whenWhereFieldErrors.postcode}
                    aria-describedby={whenWhereFieldErrors.postcode ? postcodeFieldErrorId : undefined}
                    className={`${inputClass} ${whenWhereFieldErrors.postcode ? inputErrorClass : ''}`.trim()}
                  />
                  {whenWhereFieldErrors.postcode ? (
                    <p
                      id={postcodeFieldErrorId}
                      role="alert"
                      className="m-0 font-body text-xs leading-xs text-red-700"
                    >
                      {!postcode.trim()
                        ? 'Enter the four-digit post code.'
                        : whenWhereFieldErrors.postcodeMismatch
                          ? 'Postcode does not match the selected state.'
                          : 'Enter a valid four-digit Australian post code.'}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {addressUnknown ? (
              <div className="flex w-full flex-col gap-[8px]">
                <label htmlFor={roughLocationId} className="font-body text-sm leading-sm text-text-tertiary-600">
                  Location <span className="text-text-secondary-700">(required)</span>
                </label>
                <textarea
                  id={roughLocationId}
                  value={roughLocation}
                  onChange={(e) =>
                    setRoughLocation(
                      sanitizeIncidentWhenWhereText(e.target.value, {
                        maxLen: INCIDENT_ROUGH_LOCATION_MAX_LEN,
                        multiline: true,
                      })
                    )
                  }
                  rows={4}
                  maxLength={500}
                  autoComplete="off"
                  required
                  aria-invalid={whenWhereFieldErrors.location}
                  aria-describedby={whenWhereFieldErrors.location ? roughLocationFieldErrorId : undefined}
                  className={`${inputClass} min-h-[100px] resize-y ${
                    whenWhereFieldErrors.location ? inputErrorClass : ''
                  }`.trim()}
                />
                {whenWhereFieldErrors.location ? (
                  <p
                    id={roughLocationFieldErrorId}
                    role="alert"
                    className="m-0 font-body text-xs leading-xs text-red-700"
                  >
                    Describe where the incident happened (for example suburb, road name, or landmark).
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex w-full flex-col gap-[8px]">
              <label htmlFor={stateSelectId} className="font-body text-sm leading-sm text-text-tertiary-600">
                State
              </label>
              <select
                id={stateSelectId}
                aria-describedby={whenWhereFieldErrors.state ? stateFieldErrorId : undefined}
                aria-invalid={whenWhereFieldErrors.state || whenWhereFieldErrors.postcodeMismatch}
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={`${CLAIM_FORM_SELECT_CLASS} ${
                  whenWhereFieldErrors.state || whenWhereFieldErrors.postcodeMismatch ? inputErrorClass : ''
                }`.trim()}
              >
                {AU_STATES.map((s) => (
                  <option key={s.value || 'blank'} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {whenWhereFieldErrors.state ? (
                <p
                  id={stateFieldErrorId}
                  role="alert"
                  className="m-0 font-body text-xs leading-xs text-red-700"
                >
                  Select the state or territory where the incident occurred.
                </p>
              ) : null}
            </div>

            <label className="flex cursor-pointer items-start gap-[12px] rounded-lg border border-border-secondary bg-background-secondary px-[12px] py-[12px]">
              <input
                type="checkbox"
                checked={addressUnknown}
                onChange={(e) => {
                  const next = e.target.checked
                  setAddressUnknown(next)
                  setWhenWhereSubmitAttempted(false)
                  if (next) {
                    setStreet('')
                    setSuburb('')
                    setPostcode('')
                  }
                }}
                className="mt-[2px] size-[18px] shrink-0 rounded border-border-primary text-background-brand-solid focus:ring-foreground-brand-solid"
              />
              <span className="font-body text-sm leading-sm text-text-secondary-700">
                Don&apos;t know the exact address
              </span>
            </label>
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            disabled={incidentDateBeforePolicyStart}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
